import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SearchIcon, XIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Input } from '@rocco/components/input';
import {
    Popover,
    PopoverAnchor,
    PopoverContent,
} from '@rocco/components/popover';
import { Spinner } from '@rocco/components/spinner';
import { getUserOccupation } from '~/modules/user';
import type { ContactSuggestion, Participant } from '../../../types';
import { MAX_PARTICIPANTS } from '../../../utils';
import { useContactSuggestions } from '../../../hooks/use-contact-suggestions';
import { ParticipantAvatar } from '../../avatar/ParticipantAvatar';

/**
 * The input for adding a participant to a conversation. This is a combobox with
 * a dropdown of contact suggestions, and a list of participants to be added.
 * @see {@link ChatBoxSettingsMembers}
 */
export function AddParticipantInput({
    className,
    inputClassName,
    itemClassName,
    modal,
    participants,
    participantsToAdd,
    isDisabled = false,
    onParticipantsChange,
}: {
    className?: string;
    inputClassName?: string;
    itemClassName?: string;
    modal?: boolean;
    participants: Participant[];
    /**
     * The participants to be added.
     */
    participantsToAdd: ContactSuggestion[];
    /**
     * The input and buttons are disabled.
     */
    isDisabled?: boolean;
    /**
     * Callback when the participants change.
     */
    onParticipantsChange: (participants: ContactSuggestion[]) => void;
}) {
    const maxParticipants = MAX_PARTICIPANTS - participants.length;
    const [search, setSearch] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const popoverContentRef = useRef<HTMLDivElement>(null);
    const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
    const excludeMemberIDs = useMemo(() => {
        const participantMemberIDs = participants
            .filter((p) => p.Identity.__typename === 'Member')
            .map((p) => p.Identity.ID);
        const toAddMemberIDs = participantsToAdd.map((p) => p.ID);
        return [...participantMemberIDs, ...toAddMemberIDs];
    }, [participants, participantsToAdd]);
    const {
        suggestions,
        hasMore,
        isLoading,
        setSearch: setSearchSuggestions,
        loadMore,
    } = useContactSuggestions({
        limit: 50,
        initialExcludeMemberIDs: excludeMemberIDs,
    });

    const handleSearchChange = useCallback(
        (search: string, excludeMemberIDs?: number[]) => {
            setSearch(search);
            setSearchSuggestions(search, excludeMemberIDs);
        },
        [setSearchSuggestions],
    );

    function addParticipant(contact: ContactSuggestion) {
        if (
            participantsToAdd.some((p) => p.ID === contact.ID) ||
            participants.some(
                ({ Identity }) =>
                    Identity.__typename === 'Member' &&
                    Identity.ID === contact.ID,
            ) ||
            participantsToAdd.length >= maxParticipants
        ) {
            return;
        }
        const newParticipants = [...participantsToAdd, contact];
        onParticipantsChange(newParticipants);
        handleSearchChange('', [...excludeMemberIDs, contact.ID]);
        searchInputRef.current?.focus();
        setIsOpen(false);
    }

    function removeParticipant(contact: ContactSuggestion) {
        const newParticipants = participantsToAdd.filter(
            (p) => p.ID !== contact.ID,
        );
        onParticipantsChange(newParticipants);
        handleSearchChange(
            search,
            excludeMemberIDs.filter((id) => id !== contact.ID),
        );
        searchInputRef.current?.focus();
        setIsOpen(false);
    }

    // Trigger load more suggestions when the user scrolls to the bottom.
    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        const scrollContainer = popoverContentRef.current;
        if (!sentinel || !scrollContainer || !hasMore || isLoading) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry?.isIntersecting && hasMore && !isLoading) {
                    loadMore();
                }
            },
            { root: scrollContainer },
        );
        observer.observe(sentinel);

        return () => {
            observer.disconnect();
        };
    }, [hasMore, isLoading, loadMore]);

    return (
        <div className={cn('flex flex-col gap-[8px]', className)}>
            <Popover
                open={isOpen}
                modal={modal}
                onOpenChange={(open) => {
                    if (
                        !open &&
                        document.activeElement === searchInputRef.current
                    ) {
                        // Prevent popover from closing when the input is focused.
                        return;
                    }
                    setIsOpen(open);
                }}
            >
                <PopoverAnchor asChild>
                    <Input
                        ref={searchInputRef}
                        className={cn(
                            '!rounded-full border-black/70 px-[16px] py-[8px] pr-[32px] text-[16px] leading-tight text-black placeholder:text-gray-500 md:text-[14px]',
                            inputClassName,
                        )}
                        containerClassName="w-full"
                        color="transparent"
                        placeholder="Who would you like to add?"
                        autoComplete="off"
                        autoFocus={participantsToAdd.length === 0}
                        disabled={isDisabled}
                        showClearButton
                        postfix={
                            <SearchIcon className="size-[16px] opacity-70" />
                        }
                        value={search}
                        onFocus={() => setIsOpen(true)}
                        onBlur={(e) => {
                            if (
                                popoverContentRef.current?.contains(
                                    e.relatedTarget,
                                )
                            ) {
                                // Do not close if focused on popover content.
                                return;
                            }
                            setIsOpen(false);
                        }}
                        onChange={(e) => {
                            handleSearchChange(
                                e.target.value,
                                excludeMemberIDs,
                            );
                            setIsOpen(true);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Tab' || e.key === 'ArrowDown') {
                                const listItem =
                                    popoverContentRef.current?.querySelector<HTMLElement>(
                                        '[tabIndex="0"]',
                                    );
                                if (listItem) {
                                    e.preventDefault();
                                    listItem.focus();
                                }
                            }
                        }}
                    />
                </PopoverAnchor>
                <PopoverContent
                    ref={popoverContentRef}
                    className={cn(
                        'flex max-h-[calc(var(--radix-popover-content-available-height)-1rem)] w-[var(--radix-popover-trigger-width)] flex-col overflow-y-auto overscroll-contain rounded-[8px] bg-[#f2f2f2] p-0',
                        // Prevent adding more than the maximum number of participants.
                        participantsToAdd.length >= maxParticipants && 'hidden',
                    )}
                    variant="shadcn"
                    background="none"
                    align="start"
                    side="bottom"
                    avoidCollisions={false}
                    // Prevent popover from getting focus (input should be focused).
                    onOpenAutoFocus={(e) => e.preventDefault()}
                >
                    <ul className="relative flex flex-col">
                        {suggestions.length ? (
                            <>
                                {suggestions.map((suggestion) => (
                                    <ContactListItem
                                        key={suggestion.ID}
                                        suggestion={suggestion}
                                        onClick={() =>
                                            addParticipant(suggestion)
                                        }
                                    />
                                ))}
                                {hasMore && (
                                    <div
                                        ref={loadMoreSentinelRef}
                                        className="invisible absolute inset-x-0 bottom-[500px] h-px"
                                        aria-hidden="true"
                                    ></div>
                                )}
                            </>
                        ) : (
                            <li className="flex h-[40px] items-center justify-center text-center text-[16px] leading-tight text-gray-600">
                                {isLoading ? (
                                    <Spinner className="size-[20px]" />
                                ) : (
                                    'No suggestions found'
                                )}
                            </li>
                        )}
                    </ul>
                </PopoverContent>
            </Popover>
            {participantsToAdd.length >= maxParticipants && !!search && (
                <div className="px-[4px] py-[2px] text-[12px] text-red-600">
                    {maxParticipants > 0
                        ? `You can only add up to ${maxParticipants} more participant(s)`
                        : `You cannot add any more participants`}
                </div>
            )}
            {participantsToAdd.length > 0 && (
                <ul className="flex min-h-0 flex-1 flex-wrap content-start gap-[4px] overflow-y-auto overscroll-contain">
                    {participantsToAdd.map((participant) => (
                        <li
                            key={participant.ID}
                            className={cn(
                                'flex items-center gap-[8px] rounded-[8px] bg-gray-100 p-[4px]',
                                itemClassName,
                            )}
                        >
                            <ParticipantAvatar
                                className="size-[28px]"
                                fallbackClassName="!text-[13px]"
                                participant={participant}
                            />
                            <div className="min-w-0 flex-1 truncate text-[12px] font-medium leading-tight">
                                {participant.Name}
                            </div>
                            <button
                                className="self-start p-[2px] disabled:pointer-events-none disabled:opacity-50"
                                disabled={isDisabled}
                                onClick={() => removeParticipant(participant)}
                            >
                                <XIcon
                                    className="size-[12px] text-gray-600"
                                    strokeWidth={2.5}
                                />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function ContactListItem({
    suggestion,
    onClick,
}: {
    suggestion: ContactSuggestion;
    onClick: () => void;
}) {
    const elementRef = useRef<HTMLLIElement>(null);

    return (
        <li
            ref={elementRef}
            className="flex items-center gap-[8px] p-[8px] hover:bg-black/10 focus-visible:bg-black/10"
            role="button"
            tabIndex={0}
            aria-label="Add to conversation"
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    const nextItem = elementRef.current
                        ?.nextElementSibling as HTMLElement | null;
                    if (nextItem) {
                        nextItem.focus();
                    }
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    const previousItem = elementRef.current
                        ?.previousElementSibling as HTMLElement | null;
                    if (previousItem) {
                        previousItem.focus();
                    }
                }
            }}
        >
            <ParticipantAvatar
                className="size-[36px]"
                fallbackClassName="!text-[14px]"
                participant={suggestion}
                showUserStatus
            />
            <div className="min-w-0 flex-1 space-y-[2px] leading-tight">
                <div className="truncate text-[14px] font-medium">
                    {suggestion.Name}
                </div>
                <div className="truncate text-[12px] opacity-70">
                    {getUserOccupation(suggestion)}
                </div>
            </div>
        </li>
    );
}
